import React, { useState } from 'react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WarningIcon from '@mui/icons-material/Warning';
import { IconButton, Typography, CircularProgress, Box, Tooltip, Snackbar, Alert } from "@mui/material";
import { styled } from "@mui/material/styles";
import { apiServer, urlBase } from "../../../api";
import { formatarDataComMesExtenso } from "../../../common/utils/formatadorDataHora";
import { useLogin } from "../../../contexts/LoginContext";
import { IArquivo } from "../../../types/arquivo";

const BFArquivo = styled(Box)(({ theme }) => ({
    height: '80px',
    marginTop: '10px',
    marginLeft: '5px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-2px)',
        borderColor: theme.palette.primary.main,
        boxShadow: theme.shadows[1],
    },
}));

const BFTituloArquivo = styled(Typography)(({ theme }) => ({
    padding: '3px',
    fontWeight: 600,
    color: theme.palette.text.primary,
}));

const BFDataArquivo = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: "8pt",
}));

const BFDivNome = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '12px',
    flex: 1,
});

const IconeArquivo = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    color: theme.palette.primary.main,
}));

const Arquivo = (arquivo: IArquivo) => {
    const [login] = useLogin();
    const [baixandoArquivo, setBaixandoArquivo] = useState<boolean>(false);
    const [snackbarAberto, setSnackbarAberto] = useState<boolean>(false);
    const [snackbarMsg, setSnackbarMsg] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');

    const cliqueBaixarArquivo = () => {
        setBaixandoArquivo(true);
        apiServer<string>().get(`/arquivo/solicitar-arquivo/${arquivo.id}`, login.accessToken)
            .then(response => {
                const link = document.createElement('a');
                link.href = `${urlBase}/arquivo/baixar-arquivo/${response.data}`;
                link.target = '_blank';
                link.download = arquivo.nome;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setBaixandoArquivo(false);
                setSnackbarMsg(`Download de ${arquivo.nome} iniciado.`);
                setSnackbarSeverity('success');
                setSnackbarAberto(true);
            })
            .catch(err => {
                const mensagemServidor = err?.response?.data?.mensagem;
                const msgFinal = mensagemServidor || 'Não foi possível baixar o arquivo. Verifique se o arquivo físico está disponível no servidor.';
                setSnackbarMsg(msgFinal);
                setSnackbarSeverity('error');
                setSnackbarAberto(true);
                setBaixandoArquivo(false);
            });
    };

    const isAnomalo = !!arquivo.statusAnomalia;
    const textoTooltip = arquivo.statusAnomalia === 'anomalo_queda'
        ? 'Atenção: Este arquivo apresentou uma queda abrupta de tamanho em relação aos backups anteriores.'
        : 'Atenção: Este arquivo apresentou um aumento abrupto de tamanho em relação aos backups anteriores.';

    return (
        <>
            <BFArquivo sx={isAnomalo ? { borderColor: 'warning.light', backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.05)' : '#fffdf6' } : {}}>
                <IconeArquivo>
                    <InsertDriveFileIcon sx={{ fontSize: '32px' }} />
                </IconeArquivo>
                <BFDivNome>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BFTituloArquivo variant="body2">{arquivo.descricao}</BFTituloArquivo>
                        {isAnomalo && (
                            <Tooltip title={textoTooltip} arrow>
                                <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                            </Tooltip>
                        )}
                    </Box>
                    <BFDataArquivo variant="body2">{formatarDataComMesExtenso(arquivo.dataUltimaModificacao)}</BFDataArquivo>
                </BFDivNome>
                <Box sx={{ marginRight: '16px' }}>
                    {baixandoArquivo ? (
                        <CircularProgress size={24} />
                    ) : (
                        <IconButton onClick={cliqueBaixarArquivo} color="primary">
                            <FileDownloadIcon />
                        </IconButton>
                    )}
                </Box>
            </BFArquivo>

            <Snackbar
                open={snackbarAberto}
                autoHideDuration={6000}
                onClose={() => setSnackbarAberto(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarAberto(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMsg}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Arquivo;